import React, { useState, useEffect } from 'react';
import { configData2 } from './ConfigComponents';
import "./ConfigForm.css"
import { Box, TextField } from '@mui/material';
import { DashboardSelect } from '../Input/Input';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleMinus, faTrash } from '@fortawesome/free-solid-svg-icons';

interface SubSection {
    sectionHeading: string;
    inputFields: InputField[];
}

interface InputField {
    child: any;
    name: string;
}

interface ConfigCardProps {
    inputName?: any;
    sectionName?: any;
    activeOptionKey?: any;
    sectionData?: any;
    setSectionData?: any
}

interface CheckboxOption {
    [key: string]: string;
}

interface Keys {
    [key: string]: string;
}

const InputForm: React.FC<ConfigCardProps> = ({ sectionData, setSectionData, activeOptionKey, inputName, sectionName }) => {
    const [field, setField] = useState({ name: inputName });
    const [newOptionValue, setNewOptionValue] = useState<string>("");
    const [newOptionTitle, setNewOptionTitle] = useState<string>("");
    const [newKeys, setNewKeys] = useState<boolean>(false);
    const [numKeys, setNumKeys] = useState<number>(0);
    const [enteredKeys, setEnteredKeys] = useState<string[]>([]);

    const LOCAL_STORAGE_KEY = 'configData';
    const storedDataString = localStorage.getItem(LOCAL_STORAGE_KEY) || configData2;
    let configData: any;
    var storedData = typeof storedDataString === 'string' ? JSON.parse(storedDataString) : storedDataString;
    useEffect(() => {
        configData = storedData?.[activeOptionKey];
        setField({ ...field, name: inputName });
        setEnteredKeys([])
        setNumKeys(0)
        setNewKeys(false)
    }, [activeOptionKey, inputName]);


    const handleDescriptionChange = (name: string, value: string) => {
        setSectionData((prevSectionData:any) => {
            return {
              ...prevSectionData,
              configurations: prevSectionData.configurations.map((config:any) => {
                if (config.name === name) {
                  return { ...config, description: value };
                }
                return config;
              }),
            };
          });
    };

    const handleRenderChange = (name: string, renderField: string, value: any) => {
        setSectionData((prevSectionData:any) => {
            return {
              ...prevSectionData,
              configurations: prevSectionData.configurations.map((config:any) => {
                if (config.name === name) {
                  let updatedRender = { ...config.render };
        
                  if (renderField === "type") {
                    updatedRender.extendedAttributes = {};
                    const configType = typeMapping(value);
                    config.type = configType;
                    if (configType === "list") {
                      config.value = [];
                    }
                    if (configType === "object") {
                        config.value = {};
                      }
                  }
        
                  if (!updatedRender.extendedAttributes) {
                    updatedRender.extendedAttributes = {};
                  }
        
                  if (renderField === "select" && !updatedRender.extendedAttributes.optionConfig) {
                    updatedRender.extendedAttributes.optionConfig = { options: [] };
                  }
        
                  if (renderField === "configKey" || renderField === "configName" || renderField === "source") {
                    updatedRender.extendedAttributes.optionConfig = {
                      ...updatedRender.extendedAttributes.optionConfig,
                      [renderField === "configKey" ? "key" : renderField]: value
                    };
                  }
        
                  if (renderField === "fetchOption") {
                    updatedRender.extendedAttributes.optionConfig = {
                      ...updatedRender.extendedAttributes.optionConfig,
                      fetchOption: value
                    };
                    if (value === "true") {
                      const { options, ...newOptionConfig } = updatedRender.extendedAttributes.optionConfig;
                      updatedRender.extendedAttributes.optionConfig = newOptionConfig;
                    } else if (value === "false") {
                      const { fetchType, ...updatedOptionConfig } = updatedRender.extendedAttributes.optionConfig;
                      updatedRender.extendedAttributes.optionConfig = updatedOptionConfig;
                    }
                  }
        
                  if (renderField === "fetchType") {
                    const { options, ...newOptionConfig } = updatedRender.extendedAttributes.optionConfig;
                    updatedRender.extendedAttributes.optionConfig = {
                      ...newOptionConfig,
                      fetchType: value
                    };
                  }
        
                  if (renderField === "height" || renderField === "width" || renderField === "label" || renderField === "max" || renderField === "numberOfimg" || renderField === "textType" || renderField === "key") {
                    updatedRender.extendedAttributes = {
                      ...updatedRender.extendedAttributes,
                      [renderField]: value
                    };
                  } else {
                    updatedRender = {
                      ...updatedRender,
                      [renderField]: value
                    };
                  }
        
                  return {
                    ...config,
                    render: updatedRender
                  };
                }
                return config;
              }),
            };
          });
    };

    const typeMapping = (renderType: string) => {
        switch (renderType) {
            case 'image':
                return 'string';
            case 'imageList':
                return 'list';
            case 'checkbox':
                return 'boolean';
            case 'textfield':
                return 'string';
            case 'select':
                return 'string';
            case 'track':
                return 'list';
            case 'trackOrder':
                return 'list';
            case 'labelInput':
                return 'object';
            case 'multipleCheckbox':
                return 'list';
            case 'textfieldList':
                return 'list';
            default:
                return '';
        }
    };

    const handleCheckboxOptionChange = (name: string, optionIndex: number, key: string, value: string) => {
        setSectionData((prevSectionData:any) => {
            return {
              ...prevSectionData,
              configurations: prevSectionData.configurations.map((config:any) => {
                if (config.name === name) {
                  const options = config.render.extendedAttributes.checkboxOptions || [];
                  const updatedOptions = options.map((option:CheckboxOption, idx:number) => {
                    if (idx === optionIndex) {
                      return {
                        ...option,
                        [key]: value,
                      };
                    }
                    return option;
                  });
        
                  return {
                    ...config,
                    render: {
                      ...config.render,
                      extendedAttributes: {
                        ...config.render.extendedAttributes,
                        checkboxOptions: updatedOptions,
                      },
                    },
                  };
                }
                return config;
              }),
            };
          });
    };

    const addCheckboxOption = (name: string) => {
        setSectionData((prevSectionData:any) => {
            return {
              ...prevSectionData,
              configurations: prevSectionData.configurations.map((config:any) => {
                if (config.name === name) {
                  const options = config.render.extendedAttributes?.checkboxOptions || [];
                  const CheckboxOption:any = {};
        
                  if (options.length > 0 && options[0]) {
                    Object.keys(options[0]).forEach((key) => {
                      CheckboxOption[key] = "";
                    });
                  }
        
                  let newOption = {};
                  if (options.length > 0) {
                    newOption = CheckboxOption;
                  } else {
                    const result:any = {};
                    enteredKeys.forEach((key) => {
                      result[key] = "";
                    });
                    newOption = result;
                  }
        
                  return {
                    ...config,
                    render: {
                      ...config.render,
                      extendedAttributes: {
                        ...config.render.extendedAttributes,
                        checkboxOptions: [...options, newOption],
                      },
                    },
                  };
                }
                return config;
              }),
            };
          });
          setNewKeys(false);
    };

    const getRenderPropertyByName = (name: string, property: string) => {
        const config = sectionData.configurations.find((config:any) => config.name === name);
        if (config) {
            const properties = property.split(".");
            let value = config.render;
            for (const prop of properties) {
                if (value[prop] === undefined) {
                    return "";
                }
                value = value[prop];
            }
            return value;
        }
        return "";
    };

    const getConfigTypeByName = (name: string) => {
        const config = sectionData.configurations.find((config:any) => config.name === name);
        return config?.render?.type || "";
    };

    const handleNewOption = (name: string, newValue: string, newTitle: string) => {
        setSectionData((prevSectionData:any) => {
            return {
              ...prevSectionData,
              configurations: prevSectionData.configurations.map((config:any) => {
                if (config.name === name) {
                  const options = config.render.extendedAttributes.optionConfig?.options || [];
                  return {
                    ...config,
                    render: {
                      ...config.render,
                      extendedAttributes: {
                        ...config.render.extendedAttributes,
                        optionConfig: {
                          ...config.render.extendedAttributes.optionConfig,
                          options: [...options, { value: newValue, title: newTitle }]
                        }
                      }
                    }
                  };
                }
                return config;
              }),
            };
          });
    };

    const handleDeleteOption = (fieldName: any, optionIndex: any) => {
        setSectionData((prevSectionData:any) => {
            return {
              ...prevSectionData,
              configurations: prevSectionData.configurations.map((config:any) => {
                if (config.name === fieldName) {
                  let updatedRender = { ...config.render };
        
                  if (updatedRender.extendedAttributes && updatedRender.extendedAttributes.optionConfig) {
                    const updatedOptions = [...updatedRender.extendedAttributes.optionConfig.options];
                    updatedOptions.splice(optionIndex, 1);
                    updatedRender.extendedAttributes.optionConfig.options = updatedOptions;
                  }
        
                  return {
                    ...config,
                    render: updatedRender
                  };
                }
                return config;
              }),
            };
          });
    };

    const handleOptionTitleChange = (fieldName: any, optionIndex: any, newTitle: any) => {
        setSectionData((prevSectionData:any) => {
            return {
              ...prevSectionData,
              configurations: prevSectionData.configurations.map((config:any) => {
                if (config.name === fieldName) {
                  const updatedRender = { ...config.render };
        
                  if (updatedRender.extendedAttributes && updatedRender.extendedAttributes.optionConfig) {
                    const updatedOptions = [...updatedRender.extendedAttributes.optionConfig.options];
                    updatedOptions.splice(optionIndex, 1);
                    updatedRender.extendedAttributes.optionConfig.options = updatedOptions;
                  }
        
                  return {
                    ...config,
                    render: updatedRender
                  };
                }
                return config;
              }),
            };
          });
    };

    const deleteCheckboxOption = (name: any, optionIndex: any) => {
        setSectionData((prevSectionData:any) => {
            return {
              ...prevSectionData,
              configurations: prevSectionData.configurations.map((config:any) => {
                if (config.name === name) {
                  const options = config.render.extendedAttributes?.checkboxOptions || [];
                  options.splice(optionIndex, 1);
                  return {
                    ...config,
                    render: {
                      ...config.render,
                      extendedAttributes: {
                        ...config.render.extendedAttributes,
                        checkboxOptions: [...options],
                      },
                    },
                  };
                }
                return config;
              }),
            };
          });
    };

    const handleNumKeysChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseInt(event.target.value);
        setNumKeys(value);
    };

    const handleKeyChange = (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
        const { value } = event.target;
        const updatedKeys = [...enteredKeys];
        updatedKeys[index] = value;
        setEnteredKeys(updatedKeys);
    };

    return (
        <>
            <form className="form-container">
                <div className='sectionLabels'>
                    <Box sx={{ paddingTop: '20px' }}>
                    <TextField size='small' value={field.name} label="Config Name" name='name' InputProps={{ readOnly:true
                        }} />
                    </Box>
                    <TextField size='small' value={sectionData.configurations.find((config:any) => config.name === field.name)?.render?.inputHeading || ""} label="Config Heading" onChange={(e) => handleRenderChange(field.name, 'inputHeading', e.target.value)} name='name' />
                    <TextField size='small' value={sectionData.configurations.find((config:any) => config.name === field.name)?.description || ""} label="Description" onChange={(e) => handleDescriptionChange(field.name, e.target.value)} name='name' />
                    <label className='sectionLabelHeading'>
                        <p className='sectionLabel'>UI Type:</p>
                        <div style={{ width: "50%" }}>
                            <DashboardSelect
                                onChange={(e) => handleRenderChange(field.name, 'type', e.target.value)}
                                value={getConfigTypeByName(field.name)}
                                name={field.name}
                                options={[
                                    { title: "Textfield", value: "textfield" },
                                    { title: "Image", value: "image" },
                                    { title: "Image List", value: "imageList" },
                                    { title: "Checkbox", value: "checkbox" },
                                    { title: "Color Input", value: "colorInput" },
                                    { title: "Multiple Checkbox", value: "multipleCheckbox" },
                                    { title: "Textfield List", value: "textfieldList" },
                                    { title: "Select", value: "select" },
                                    { title: "Label Input", value: "labelInput" },
                                    { title: "Track", value: "track" },
                                    { title: "Track Order", value: "trackOrder" }
                                ]}
                                selectLableId=""
                            />
                        </div>
                    </label>
                    {getConfigTypeByName(field.name) === 'image' && (
                        <div className='sectionLabels'>
                            <label className='sectionLabelHeading'>
                                <p className='sectionLabel'> Enter Label:</p>

                                <input
                                    className='inputText'
                                    type="text"
                                    value={getRenderPropertyByName(field.name, "extendedAttributes.label") || ""}
                                    placeholder="Enter Label"
                                    onChange={(e) => handleRenderChange(field.name, 'label', e.target.value)}
                                />
                            </label>
                            <label className='sectionLabelHeading'>
                                <p className='sectionLabel'> Enter Height:</p>
                                <input
                                    className='inputText'
                                    type="text"
                                    value={getRenderPropertyByName(field.name, "extendedAttributes.height") || ""}
                                    placeholder="Enter Height"
                                    onChange={(e) => handleRenderChange(field.name, 'height', e.target.value)}
                                />
                            </label>
                            <label className='sectionLabelHeading'>
                                <p className='sectionLabel'> Enter Width:</p>
                                <input
                                    className='inputText'
                                    type="text"
                                    value={getRenderPropertyByName(field.name, "extendedAttributes.width") || ""}
                                    placeholder="Enter Width"
                                    onChange={(e) => handleRenderChange(field.name, 'width', e.target.value)}
                                />
                            </label>
                            <label className='sectionLabelHeading'>
                                <p className='sectionLabel'> Do you want maximum size image validation:</p>
                                <input
                                    type="checkbox"
                                    checked={getRenderPropertyByName(field.name, "extendedAttributes.max") === "true"}
                                    onChange={(e) => handleRenderChange(field.name, 'max', e.target.checked ? "true" : "false")}
                                />
                            </label>
                        </div>
                    )}
                    {getConfigTypeByName(field.name) === 'imageList' && (
                        <div className='sectionLabels'>
                            <label className='sectionLabelHeading'>
                                <p className='sectionLabel'> Enter Label:</p>
                                <input
                                    className='inputText'
                                    type="text"
                                    value={getRenderPropertyByName(field.name, "extendedAttributes.label") || ""}
                                    placeholder="Enter Label"
                                    onChange={(e) => handleRenderChange(field.name, 'label', e.target.value)}
                                />
                            </label>
                            <label className='sectionLabelHeading'>
                                <p className='sectionLabel'>Enter Height:</p>
                                <input
                                    className='inputText'
                                    type="text"
                                    value={getRenderPropertyByName(field.name, "extendedAttributes.height") || ""}
                                    placeholder="Enter Height"
                                    onChange={(e) => handleRenderChange(field.name, 'height', e.target.value)}
                                />
                            </label>
                            <label className='sectionLabelHeading'>
                                <p className='sectionLabel'> Enter Width:</p>
                                <input
                                    className='inputText'
                                    type="text"
                                    value={getRenderPropertyByName(field.name, "extendedAttributes.width") || ""}
                                    placeholder="Enter Width"
                                    onChange={(e) => handleRenderChange(field.name, 'width', e.target.value)}
                                />
                            </label>
                            <label className='sectionLabelHeading'>
                                <p className='sectionLabel'> Enter No. of Images:</p>
                                <input
                                    className='inputText'
                                    type="text"
                                    value={getRenderPropertyByName(field.name, "extendedAttributes.numberOfimg") || ""}
                                    placeholder="Enter No. of Images"
                                    onChange={(e) => handleRenderChange(field.name, 'numberOfimg', e.target.value)}
                                />
                            </label>
                            <label className='sectionLabelHeading'>
                                <p className='sectionLabel'> Do you want maximum size image validation:</p>
                                <input
                                    type="checkbox"
                                    checked={getRenderPropertyByName(field.name, "extendedAttributes.max") === "true"}
                                    onChange={(e) => handleRenderChange(field.name, 'max', e.target.checked ? "true" : "false")}
                                />
                            </label>
                        </div>
                    )}
                    {getConfigTypeByName(field.name) === 'textfield' && (
                        <div className='sectionLabels'>
                            <label className='sectionLabelHeading'>
                                <p className='sectionLabel'> Type of textfield:</p>
                                <div style={{ width: "50%" }}>
                                    <DashboardSelect
                                        onChange={(e) => handleRenderChange(field.name, 'textType', e.target.value)}
                                        value={getRenderPropertyByName(field.name, "extendedAttributes.textType") || "string"}
                                        name={field.name}
                                        options={[
                                            "string", "number"
                                        ]}
                                        selectLableId="yourLabelId"
                                    />
                                </div>

                            </label>
                        </div>
                    )}
                    {getConfigTypeByName(field.name) === 'checkbox' && (
                        <div>
                            <label className='sectionLabelHeading'>
                                <p className='sectionLabel'> Label of checkbox:</p>
                                <input
                                    className='inputText'
                                    type="text"
                                    value={getRenderPropertyByName(field.name, "extendedAttributes.label") || ""}
                                    placeholder="Label of checkbox"
                                    onChange={(e) => handleRenderChange(field.name, 'label', e.target.value)}
                                />
                            </label>
                        </div>
                    )}
                    {getConfigTypeByName(field.name) === 'multipleCheckbox' && (
                        <div>
                            {getRenderPropertyByName(field.name, "extendedAttributes.checkboxOptions") && <label className='sectionLabelHeading'>
                                <p className='sectionLabel'> Key for showing checkbox options:</p>
                                <input
                                    className='inputText'
                                    type="text"
                                    value={getRenderPropertyByName(field.name, "extendedAttributes.key") || ""}
                                    placeholder="Key for showing checkbox options"
                                    onChange={(e) => handleRenderChange(field.name, 'key', e.target.value)}
                                />
                            </label>}

                            {Array.isArray(getRenderPropertyByName(field.name, "extendedAttributes.checkboxOptions")) &&
                                getRenderPropertyByName(field.name, "extendedAttributes.checkboxOptions").map((option: CheckboxOption, optionIndex: number) => (
                                    <div style={{ display: "flex", padding: "10px", gap: "10px", alignItems: "center" }} key={optionIndex}>
                                        {Object.keys(option).map((key, keyIndex) => (
                                            <div key={keyIndex}>
                                                <TextField size='small' value={option[key]} label={key} onChange={(e) => handleCheckboxOptionChange(field.name, optionIndex, key, e.target.value)} variant="filled" />
                                            </div>
                                        ))}
                                        <FontAwesomeIcon
                                            onClick={() => deleteCheckboxOption(field.name, optionIndex)}
                                            className="footer_config_plus_icon"
                                            icon={faCircleMinus}
                                        />
                                    </div>
                                ))}
                            {newKeys &&
                                <div>
                                    <label style={{ display: "flex", alignItems: "center", gap: "15px", marginBottom: "20px" }} className='sectionLabelHeading' htmlFor="numKeysInput">
                                        <p className='labelHeading'>
                                            Number of keys:
                                        </p>
                                        <input
                                            className='inputText'
                                            id="numKeysInput"
                                            type="number"
                                            value={numKeys}
                                            onChange={handleNumKeysChange}
                                        />
                                    </label>
                                    {numKeys > 0 && (
                                        <div style={{ display: "flex" }}>
                                            {Array.from({ length: numKeys }, (_, index) => (
                                                <input
                                                    className='inputText'
                                                    key={index}
                                                    type="text"
                                                    placeholder={`Key ${index + 1}`}
                                                    onChange={(event) => handleKeyChange(event, index)}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            }
                            {(getRenderPropertyByName(field.name, "extendedAttributes.checkboxOptions") || enteredKeys.length > 0) &&
                                <button className="uiButton" type="button" onClick={() => addCheckboxOption(field.name)}>Add Checkbox Option</button>
                            }
                            {!getRenderPropertyByName(field.name, "extendedAttributes.checkboxOptions") && <button className="uiButton" type="button" onClick={() => setNewKeys(true)}>Add keys</button>}

                        </div>
                    )}
                    {getConfigTypeByName(field.name) === 'select' && (
                        <div>
                            <p className='labelHeading' style={{ display: "flex" }}>Options :</p>
                            <label className="custom-checkbox-label">
                                <input
                                    type="checkbox"
                                    className="custom-checkbox-input"
                                    checked={getRenderPropertyByName(field.name, "extendedAttributes.optionConfig.fetchOption") === "true"}
                                    onChange={(e) => handleRenderChange(field.name, 'fetchOption', e.target.checked ? "true" : "false")}
                                />
                                <span
                                    className={`custom-checkbox-span ${getRenderPropertyByName(field.name, "extendedAttributes.optionConfig.fetchOption") === "true" ? 'checked' : ''}`}
                                ></span>
                                Do you want to fetch option from somewhere
                            </label>
                            {getRenderPropertyByName(field.name, "extendedAttributes.optionConfig.fetchOption") === "true" && <DashboardSelect
                                onChange={(e) => handleRenderChange(field.name, 'fetchType', e.target.value)}
                                value={getRenderPropertyByName(field.name, "extendedAttributes.optionConfig.fetchType")}
                                name={field.name}
                                options={[
                                    { title: "Config", value: "config" },
                                    { title: "Country", value: "country" },
                                    { title: "TimeZone", value: "timeZone" }
                                ]}
                                selectLableId=""
                            />}
                            {getRenderPropertyByName(field.name, "extendedAttributes.optionConfig.fetchType") === "config" &&
                                <div className='sectionLabels'>
                                    <Box sx={{ paddingTop: '20px' }}>
                                        <TextField size='small' value={getRenderPropertyByName(field.name, "extendedAttributes.optionConfig.source") || ""} label="Source Config" onChange={(e) => handleRenderChange(field.name, 'source', e.target.value)} name='name' />
                                    </Box>
                                    <TextField size='small' value={getRenderPropertyByName(field.name, "extendedAttributes.optionConfig.configName") || ""} label="Config Name" onChange={(e) => handleRenderChange(field.name, 'configName', e.target.value)} name='name' />
                                    <TextField size='small' value={getRenderPropertyByName(field.name, "extendedAttributes.optionConfig.key") || ""} label="Key" onChange={(e) => handleRenderChange(field.name, 'configKey', e.target.value)} name='name' />
                                </div>}
                            {(!getRenderPropertyByName(field.name, "extendedAttributes.optionConfig.fetchOption") || getRenderPropertyByName(field.name, "extendedAttributes.optionConfig.fetchOption") === "false") && <div>
                                <div className='optionContainer'>
                                    <input
                                        className='inputText'
                                        type="text"
                                        placeholder="Option Value"
                                        value={newOptionValue}
                                        onChange={(e) => setNewOptionValue(e.target.value)}
                                    />
                                    <input
                                        className='inputText'
                                        type="text"
                                        placeholder="Option Title"
                                        value={newOptionTitle}
                                        onChange={(e) => setNewOptionTitle(e.target.value)}
                                    />
                                    <button
                                        className="uiButton"
                                        type="button"
                                        onClick={() => {
                                            if (newOptionValue.trim() !== '' && newOptionTitle.trim() !== '') {
                                                handleNewOption(field.name, newOptionValue, newOptionTitle);
                                                setNewOptionValue('');
                                                setNewOptionTitle('');
                                            }
                                        }}
                                    >
                                        Add Option
                                    </button>
                                </div>
                                {Array.isArray(getRenderPropertyByName(field.name, "extendedAttributes.optionConfig.options")) && (
                                    <div>
                                        <label className='sectionLabelHeading' style={{ paddingBlock: "10px" }}>Already Available Option :</label>
                                        <div className="optionsList">
                                            {getRenderPropertyByName(field.name, "extendedAttributes.optionConfig.options").map((option: any, optionIndex: number) => (
                                                <div key={optionIndex} className='optionContainer'>
                                                    <TextField size='small' value={option.value} label="Option Value" variant="standard" InputProps={{ readOnly: true }} />
                                                    <TextField size='small' value={option.title} label="Option Title" variant="standard" onChange={(e) => handleOptionTitleChange(field.name, optionIndex, e.target.value)} />

                                                    <FontAwesomeIcon
                                                        onClick={() => handleDeleteOption(field.name, optionIndex)}
                                                        className="footer_config_plus_icon"
                                                        icon={faCircleMinus}
                                                    />

                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>}

                        </div>
                    )}
                </div>
            </form>
        </>

    );
};

export default InputForm;
